import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { sendVerificationEmail } from '../../ducks/user.duck';
import css from './VerifyEmailBanner.module.css';

/**
 * "Please verify your email" bar, for any signed-in user whose address is
 * unverified.
 *
 * Sharetribe refuses to deliver ANY notification to an unverified address, by
 * design, to protect its sending reputation. That makes verification a silent
 * precondition for the whole order flow: an unverified seller is never told
 * their item sold, and an unverified buyer is charged and hears nothing - no
 * receipt, no tracking, and no delivered email, which is what opens their
 * 3-day inspection window. On 18 Sep 2026, 36 of 48 users were unverified and
 * six of them held 24 of the ~34 live listings.
 *
 * Self-contained on purpose - it reads currentUser from the store and
 * dispatches the resend itself - so it can be dropped into any nav without
 * threading props. It renders nothing at all unless it is needed, so placing
 * it is always safe.
 *
 * Deliberately not dismissible: the consequence lasts as long as the
 * unverified state does.
 */
const VerifyEmailBanner = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const resendInProgress = useSelector(state => state.user?.sendVerificationEmailInProgress);
  const [resent, setResent] = useState(false);

  const emailUnverified =
    !!currentUser?.id && currentUser?.attributes?.emailVerified === false;

  if (!emailUnverified) {
    return null;
  }

  const handleResend = () => {
    dispatch(sendVerificationEmail())
      .then(() => setResent(true))
      .catch(() => setResent(false));
  };

  return (
    <div className={css.banner} role="status">
      <span className={css.text}>
        <strong>Please verify your email.</strong> Until you do, we can't send you order
        notifications &mdash; including when your item sells, your tracking number, or your
        delivery confirmation.
      </span>
      {resent ? (
        <span className={css.sent}>Verification email sent &mdash; check your inbox</span>
      ) : (
        <button
          type="button"
          className={css.button}
          onClick={handleResend}
          disabled={resendInProgress}
        >
          {resendInProgress ? 'Sending…' : 'Resend email'}
        </button>
      )}
    </div>
  );
};

export default VerifyEmailBanner;
